import React from 'react';
import { Shield, Target, Users, Award, Clock, TrendingUp, CheckCircle, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { usePopup } from '@/context/PopupContext';
import { Testimonials } from '@/components/sections/Testimonials';

const faqs = [
  {
    question: 'How long does the trademark registration process take?',
    answer: 'The typical trademark registration process takes 8-12 months from filing to registration. However, you can start using the TM symbol immediately after filing.'
  },
  {
    question: 'What\'s included in the government filing fee?',
    answer: 'The USPTO charges a government filing fee of $250-$350 per class of goods/services. This fee is separate from our service fees and is paid directly to the government.'
  },
  {
    question: 'Can I trademark a business name?',
    answer: 'Yes, you can trademark a business name if it\'s distinctive and used in commerce. We\'ll conduct a comprehensive search to ensure your name is available for registration.'
  },
  {
    question: 'What happens if my trademark application is rejected?',
    answer: 'If your application receives an office action, our legal team will work with you to address the examiner\'s concerns and respond appropriately to overcome any objections.'
  },
  {
    question: 'Do you offer refunds?',
    answer: 'We offer a satisfaction guarantee. If we cannot successfully file your trademark application, we\'ll provide a full refund of our service fees (government fees are non-refundable).'
  },
  {
    question: 'What can be trademarked?',
    answer: 'You can trademark brand names, logos, slogans, product names, service marks, and even specific colors, sounds, or packaging designs that identify your brand.'
  },
  {
    question: 'How much does trademark registration cost?',
    answer: 'Our service fees start at $45, plus USPTO government filing fees of $250-$350 per class. We offer transparent pricing with packages to fit different needs and budgets.'
  },
  {
    question: 'Can I file a trademark myself?',
    answer: 'While you can file directly with USPTO, our expertise ensures proper classification, comprehensive searches, professional responses to office actions, and significantly higher approval rates.'
  },
  {
    question: 'What\'s the difference between TM and ®?',
    answer: 'TM indicates you\'re claiming rights to a mark (can be used immediately), while ® indicates your trademark is officially registered with the USPTO (only after registration is complete).'
  },
  {
    question: 'How long does trademark protection last?',
    answer: 'A registered trademark can last indefinitely as long as you maintain proper use and file required maintenance documents at 5-6 years, 9-10 years, and every 10 years thereafter.'
  }
];

const whyChooseUsReasons = [
  {
    icon: Clock,
    title: 'Lightning-Fast 24-Hour Filing',
    description: 'We understand time is critical for your business. Our expedited processing ensures your trademark application is filed with the USPTO within 24 hours of approval, securing your earliest possible filing date and priority rights.'
  },
  {
    icon: Award,
    title: 'Industry-Leading 98% Success Rate',
    description: 'Our expert trademark attorneys have a proven track record of success. With comprehensive searches, meticulous application preparation, and strategic office action responses, we achieve a 98% approval rate—significantly higher than industry average.'
  },
  {
    icon: Shield,
    title: 'Comprehensive Legal Protection',
    description: 'Beyond just filing, we provide ongoing monitoring, renewal reminders, and enforcement support. Our comprehensive service ensures your trademark remains protected throughout its lifetime, giving you complete peace of mind.'
  },
  {
    icon: Users,
    title: 'Dedicated Expert Team',
    description: 'Work with experienced attorneys who specialize in intellectual property law. Our team has successfully registered over 15,000 trademarks across diverse industries, bringing unmatched expertise to your brand protection.'
  },
  {
    icon: TrendingUp,
    title: 'Transparent Pricing',
    description: 'No hidden fees, no surprises. We offer clear, upfront pricing starting at just $45, making professional trademark protection accessible to businesses of all sizes.'
  },
  {
    icon: CheckCircle,
    title: 'End-to-End Service',
    description: 'From initial trademark search to final registration certificate, we handle every step of the process. You get expert guidance, regular updates, and complete support throughout your trademark journey.'
  }
];

export const AboutPage = () => {
  const { openPopup } = usePopup();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900">
      {/* Spacer for fixed navbar */}
      <div className="h-[116px]"></div>
      
      {/* Hero Section - About the Company */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/2 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-20">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent">
                About Us
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              Safeguarding Innovation, Protecting Identity
            </p>
          </div>

          {/* About the Company Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Who We Are
                </span>
              </h2>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p className="text-base md:text-lg">
                  We are a leading intellectual property law firm dedicated to helping businesses, entrepreneurs, and creators protect their most valuable assets. With over a decade of experience and more than 15,000 successful trademark registrations, we've built a reputation for excellence, reliability, and client-focused service.
                </p>
                <p className="text-base md:text-lg">
                  Our team of experienced attorneys, paralegals, and IP specialists work together to provide comprehensive trademark, copyright, and patent services. We understand that your brand and innovations represent years of hard work, and we're committed to ensuring they receive the strongest possible legal protection.
                </p>
                <p className="text-base md:text-lg">
                  From startups launching their first product to established enterprises expanding their portfolios, we serve clients across all industries with the same dedication, expertise, and attention to detail that has made us a trusted name in intellectual property protection.
                </p>
              </div>
            </div>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300"></div>
              <img
                src="https://images.unsplash.com/photo-1758518729908-d4220a678d81?q=85&w=800"
                alt="Professional team collaboration"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover border-2 border-blue-500/30 relative z-10 hover:border-blue-400/60 transition-all duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision and Mission Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden">
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Our Vision & Mission
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Vision Card */}
            <Card className="bg-gradient-to-br from-blue-950 to-slate-900 border-2 border-blue-500/40 hover:border-blue-400 hover:shadow-[0_0_40px_rgba(59,130,246,0.6)] transition-all duration-300 group">
              <CardContent className="p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-blue-500/50">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                    Our Vision
                  </h3>
                  <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                    To be the world's most trusted and accessible intellectual property protection partner, empowering innovators and creators to build lasting legacies without barriers of complexity, cost, or uncertainty.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Mission Card */}
            <Card className="bg-gradient-to-br from-purple-950 to-slate-900 border-2 border-purple-500/40 hover:border-purple-400 hover:shadow-[0_0_40px_rgba(168,85,247,0.6)] transition-all duration-300 group">
              <CardContent className="p-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-purple-400 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-purple-500/50">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                    Our Mission
                  </h3>
                  <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                    To provide exceptional intellectual property services that combine cutting-edge technology, expert legal guidance, and personalized support—making brand protection accessible, affordable, and stress-free for businesses of all sizes.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Core Values */}
          <div className="mt-20">
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-center mb-12">
              <span className="text-white">Our Core </span>
              <span className="text-blue-400">Values</span>
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center group">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-600 to-blue-400 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-blue-500/50 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-heading font-bold text-white mb-2">Integrity</h4>
                <p className="text-sm text-gray-400">Honest, transparent service in every interaction</p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-600 to-purple-400 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-purple-500/50 group-hover:scale-110 transition-transform duration-300">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-heading font-bold text-white mb-2">Excellence</h4>
                <p className="text-sm text-gray-400">Exceptional results in everything we do</p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/50 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-heading font-bold text-white mb-2">Client-Focused</h4>
                <p className="text-sm text-gray-400">Your success is our top priority</p>
              </div>
              <div className="text-center group">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-pink-600 to-pink-400 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-pink-500/50 group-hover:scale-110 transition-transform duration-300">
                  <Star className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-heading font-bold text-white mb-2">Innovation</h4>
                <p className="text-sm text-gray-400">Leveraging technology for better service</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-blue-950 via-slate-900 to-purple-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Why Choose Us
              </span>
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              With over a decade of experience and thousands of successful registrations, we provide unmatched expertise and dedication to protecting your intellectual property.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUsReasons.map((reason, idx) => (
              <Card key={idx} className="bg-gradient-to-br from-slate-900 to-blue-950 border-2 border-blue-500/40 hover:border-blue-400 hover:shadow-[0_0_40px_rgba(59,130,246,0.6)] hover:-translate-y-2 transition-all duration-300 group">
                <CardContent className="p-6 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <reason.icon className="w-12 h-12 text-blue-400 mb-4 group-hover:scale-110 transition-transform duration-300" />
                    <h3 className="text-xl font-heading font-bold text-white mb-3">
                      {reason.title}
                    </h3>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Stats Section */}
          <div className="mt-20 grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full mb-6 shadow-lg shadow-blue-500/50 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-10 h-10 text-white" />
              </div>
              <div className="text-5xl font-heading font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">15,000+</div>
              <h3 className="text-xl font-semibold text-white mb-2">Trademarks Registered</h3>
              <p className="text-sm text-gray-400">
                Successfully protecting brands across all industries
              </p>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-600 to-purple-400 rounded-full mb-6 shadow-lg shadow-purple-500/50 group-hover:scale-110 transition-transform duration-300">
                <Award className="w-10 h-10 text-white" />
              </div>
              <div className="text-5xl font-heading font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-4">98%</div>
              <h3 className="text-xl font-semibold text-white mb-2">Success Rate</h3>
              <p className="text-sm text-gray-400">
                Industry-leading approval rate for trademark applications
              </p>
            </div>

            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-600 to-cyan-400 rounded-full mb-6 shadow-lg shadow-cyan-500/50 group-hover:scale-110 transition-transform duration-300">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <div className="text-5xl font-heading font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-4">10+</div>
              <h3 className="text-xl font-semibold text-white mb-2">Years of Experience</h3>
              <p className="text-sm text-gray-400">
                Decade of specialized expertise in IP law
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* FAQs Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 right-10 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-1/4 left-10 w-64 h-64 bg-purple-500 rounded-full filter blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-6">
              <span className="bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent">
                Frequently Asked
              </span>
              <br />
              <span className="text-blue-400">Questions</span>
            </h2>
            <p className="text-lg text-gray-300">
              Find answers to common questions about our services
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-900/50 border-2 border-blue-900/30 rounded-lg px-6 hover:border-blue-500/50 transition-colors"
              >
                <AccordionTrigger className="text-left text-lg font-semibold text-white hover:text-blue-400 py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/3 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl animate-blob animation-delay-2000"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Join thousands of satisfied clients who trust us with their intellectual property protection. Get started today for just $45.
          </p>
          <Button
            onClick={() => openPopup('about')}
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 font-bold text-lg px-12 py-7 transition-all duration-300 hover:scale-105 shadow-2xl"
          >
            Get Started Now →
          </Button>
        </div>
      </section>
    </div>
  );
};
